'use client';

import {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useCallback,
} from 'react';
import { Box } from '@mui/material';
import { EmptyBox } from '@/components/EmptyBox';
import {
  api_portalOfficeListing_filter,
  service_ResponseForNonMemberPortalOfficeListing,
} from '@/init/swagger';
import { PublicListingListItem } from './PublicListingListItem';

export interface PublicListingListRef {
  scrollToListing: (listingId: number, source?: 'click' | 'hover') => void;
}

export const PublicListingList = forwardRef<
  PublicListingListRef,
  {
    listings: service_ResponseForNonMemberPortalOfficeListing[] | undefined;
    hoveredMarkerListingId?: number | null;
    setHoveredMarkerListingIdWithoutScroll?: (listingId: number | null) => void;
    isShowMap?: boolean;
    searchFilters: api_portalOfficeListing_filter[];
  }
>(
  (
    {
      listings,
      hoveredMarkerListingId,
      setHoveredMarkerListingIdWithoutScroll,
      isShowMap,
      searchFilters,
    },
    ref,
  ) => {
    const listingRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    // スクロール位置を監視してマーカーを選択する機能（Intersection Observer使用）
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastSelectedIdRef = useRef<number | null>(null); // ちらつき防止用
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // デバウンス処理用
    const isMarkerClickScrollRef = useRef<boolean>(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // スクロールコンテナを見つける関数
    const findScrollContainer = useCallback(() => {
      // 最初のリスティング要素からスクロールコンテナを探す
      const firstElement = Array.from(listingRefs.current.values())[0];
      if (!firstElement) return null;

      let parent = firstElement.parentElement;
      while (parent && parent !== document.body) {
        const computedStyle = window.getComputedStyle(parent);
        const overflow = computedStyle.overflow || computedStyle.overflowY;

        if (overflow === 'auto' || overflow === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    }, []);

    // 一番下のアイテム選択を試行
    const trySelectBottomItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        let isAtBottom = false;
        const scrollContainer = findScrollContainer();

        if (scrollContainer) {
          isAtBottom =
            scrollContainer.scrollTop + scrollContainer.clientHeight >=
            scrollContainer.scrollHeight - 5; // 5pxの余裕を持たせる
        } else {
          // フォールバック: ページ全体のスクロール位置をチェック
          isAtBottom =
            window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - 5;
        }

        if (!isAtBottom || !listings || listings.length === 0) {
          return null;
        }

        const lastListing = listings[listings.length - 1];
        if (!lastListing.id) return null;

        const lastElement = listingRefs.current.get(lastListing.id);
        if (!lastElement) return null;

        const rect = lastElement.getBoundingClientRect();
        const isVisible =
          rect.bottom > containerRect.top && rect.top < containerRect.bottom;

        return isVisible ? lastListing.id : null;
      },
      [listings, findScrollContainer],
    );

    // 一番上の完全表示アイテムを選択
    const findTopCompletelyVisibleItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        for (const listing of listings || []) {
          if (!listing.id) continue;

          const element = listingRefs.current.get(listing.id);
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const isCompletelyVisible =
            rect.top >= containerRect.top &&
            rect.bottom <= containerRect.bottom;

          if (isCompletelyVisible) {
            return listing.id;
          }
        }
        return null;
      },
      [listings],
    );

    // 一番上の表示アイテムを選択（フォールバック）
    const findTopVisibleItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        for (const listing of listings || []) {
          if (!listing.id) continue;

          const element = listingRefs.current.get(listing.id);
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const isVisible =
            rect.bottom > containerRect.top && rect.top < containerRect.bottom;

          if (isVisible) {
            return listing.id;
          }
        }
        return null;
      },
      [listings],
    );

    const handleIntersection = useCallback(
      (_entries: IntersectionObserverEntry[]) => {
        if (!isShowMap || !setHoveredMarkerListingIdWithoutScroll) {
          return;
        }

        // マーカークリックによるスクロール中は処理をスキップ
        if (isMarkerClickScrollRef.current) {
          return;
        }

        // デバウンス処理: 連続したイベントを制御
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          const scrollContainer = findScrollContainer();
          const containerRect = scrollContainer
            ? scrollContainer.getBoundingClientRect()
            : { top: 0, bottom: window.innerHeight };

          // 適切なアイテムを選択
          const selectedId =
            trySelectBottomItem(containerRect) ||
            findTopCompletelyVisibleItem(containerRect) ||
            findTopVisibleItem(containerRect);

          // 選択を更新
          if (selectedId !== null && selectedId !== lastSelectedIdRef.current) {
            lastSelectedIdRef.current = selectedId;
            setHoveredMarkerListingIdWithoutScroll(selectedId);
          }
        }, 200); // 安定性を重視して200msに調整
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isShowMap,
        setHoveredMarkerListingIdWithoutScroll,
        trySelectBottomItem,
        findTopCompletelyVisibleItem,
        findTopVisibleItem,
      ],
    );

    // Intersection Observerの設定
    useEffect(() => {
      if (!isShowMap || !setHoveredMarkerListingIdWithoutScroll) {
        // 既存のobserverがある場合は停止
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        return;
      }

      // スクロールコンテナを取得
      const scrollContainer = findScrollContainer();

      // Intersection Observerを作成（特定のスクロールコンテナ内での監視）
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: scrollContainer, // 特定のスクロールコンテナ内でのみ監視
        rootMargin: '0px',
        // より安定した閾値設定：250px高さに最適化
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
      });

      // 現在の要素を監視対象に追加
      listingRefs.current.forEach((element, listingId) => {
        if (element && observerRef.current) {
          element.setAttribute('data-listing-id', listingId.toString());
          observerRef.current.observe(element);
        }
      });

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
      };
    }, [
      isShowMap,
      setHoveredMarkerListingIdWithoutScroll,
      listings,
      handleIntersection,
      findScrollContainer,
    ]);

    useImperativeHandle(ref, () => ({
      scrollToListing: (
        listingId: number,
        source: 'click' | 'hover' = 'click',
      ) => {
        const element = listingRefs.current.get(listingId);

        if (element) {
          // マーカークリックによるスクロールの場合、一時的にObserver処理を無効化
          if (source === 'click') {
            isMarkerClickScrollRef.current = true;

            // マーカークリックされたアイテムのIDを強制的に設定
            lastSelectedIdRef.current = listingId;
            setHoveredMarkerListingIdWithoutScroll?.(listingId);

            // スクロール完了を検知してから無効化を解除
            const onScrollEnd = () => {
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
              }
              scrollTimeoutRef.current = setTimeout(() => {
                isMarkerClickScrollRef.current = false;
              }, 1000); // スクロール停止から1秒後に解除
            };

            // スクロール開始前に既存のタイマーをクリア
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }

            // スクロール開始直後に一度呼び出す
            onScrollEnd();

            // 最大5秒後には必ず無効化を解除
            setTimeout(() => {
              isMarkerClickScrollRef.current = false;
            }, 5000);
          }

          // overflow: auto を持つ親コンテナを探す
          let scrollContainer: Element | null = null;
          let parent = element.parentElement;

          while (parent) {
            const computedStyle = window.getComputedStyle(parent);
            const overflow = computedStyle.overflow || computedStyle.overflowY;

            if (overflow === 'auto' || overflow === 'scroll') {
              scrollContainer = parent;
              break;
            }
            parent = parent.parentElement;
          }

          if (scrollContainer) {
            // 親のスクロールコンテナ内での相対位置を計算
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // ホバー時とクリック時で異なるスクロール位置を設定
            let targetPosition;
            if (source === 'hover') {
              // ホバー時はより穏やかなスクロール（要素が見える程度）
              targetPosition =
                scrollContainer.scrollTop +
                (elementRect.top - containerRect.top) -
                100; // 上部に100pxのマージン
            } else {
              // クリック時は中央に配置
              targetPosition =
                scrollContainer.scrollTop +
                (elementRect.top - containerRect.top) -
                containerRect.height / 2 +
                elementRect.height / 2;
            }

            // スムーズスクロール
            scrollContainer.scrollTo({
              top: Math.max(0, targetPosition), // 0以下にならないように
              behavior: 'smooth',
            });
          } else {
            // フォールバック: 通常のscrollIntoView
            element.scrollIntoView({
              behavior: 'smooth',
              block: source === 'hover' ? 'nearest' : 'center',
              inline: 'nearest',
            });
          }
        }
      },
    }));

    const setListingRef = (
      listingId: number,
      element: HTMLDivElement | null,
    ) => {
      // 古い要素をクリーンアップ
      const oldElement = listingRefs.current.get(listingId);
      if (oldElement && observerRef.current) {
        observerRef.current.unobserve(oldElement);
      }

      if (element) {
        listingRefs.current.set(listingId, element);
        // 新しい要素をIntersection Observerに追加（マップ表示時のみ）
        if (observerRef.current && isShowMap) {
          element.setAttribute('data-listing-id', listingId.toString());
          observerRef.current.observe(element);
        }
      } else {
        listingRefs.current.delete(listingId);
      }
    };

    if (!listings) {
      return null;
    }

    return (
      <>
        {!listings?.length && (
          <Box sx={{ mx: 2 }}>
            <EmptyBox
              mainMessage="ご指定の条件に該当する看護・介護サービスが見つかりませんでした"
              subMessage="検索条件を変更して、再度検索してください。"
            />
          </Box>
        )}

        {listings?.map((listing, listingIndex) => (
          <Box
            key={listingIndex}
            mb={2}
            ref={(el) => {
              const listingId = listing.id;
              if (listingId) {
                setListingRef(listingId, el as HTMLDivElement | null);
              }
            }}
          >
            <PublicListingListItem
              listing={listing}
              isHighlighted={hoveredMarkerListingId === listing.id}
              isShowMap={isShowMap}
              searchFilters={searchFilters}
            />
          </Box>
        ))}
      </>
    );
  },
);

PublicListingList.displayName = 'PublicListingList';
